/**
 * Centralized application configuration
 * Single source of truth for all constants, addresses, timeouts, and limits
 */

// ============================================================================
// ENVIRONMENT & NETWORK CONFIGURATION
// ============================================================================

/**
 * Environment detection from build-time variable
 * Values: 'main' | 'staging' | 'testnet'
 * - main: Mainnet contracts, production deployment
 * - staging: Mainnet contracts, staging deployment for testing
 * - testnet: Testnet contracts, development deployment
 */
export const ENV = (typeof __CF_PAGES_BRANCH__ !== 'undefined' ? __CF_PAGES_BRANCH__ : 'dev') as 'main' | 'staging' | 'testnet';

/**
 * Determine if we're using mainnet (true for prod/staging) or testnet
 */
export const IS_MAINNET = ENV === 'main' || ENV === 'staging';

/* console.log('__CF_PAGES_BRANCH__', __CF_PAGES_BRANCH__);
console.log(`Running in ${ENV} environment`); // Log the detected environment for debugging purposes
console.log(`Using ${IS_MAINNET ? 'mainnet' : 'testnet'} configuration`); // Log the network configuration for debugging purposes */

/**
 * Chain configuration
 */
export interface ChainConfig {
  name: string;
  accountLabel: string;
  chainId: number;
  rpcUrl: string;
  wsRpcUrl: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
}

/**
 * Detect if running on localhost for development
 */
const IS_LOCALHOST =
  typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

/**
 * BSC Network Configurations (hardcoded to avoid importing heavy Reown library)
 * Used by Reown AppKit and internal chain config
 *
 * Uses public RPC for localhost (domain-restricted Chainstack RPC blocks localhost)
 * Uses premium Chainstack RPC for production domains (domain-restricted for security)
 */
export const BSC_MAINNET_NETWORK = {
  id: 56,
  chainId: 56,
  caipNetworkId: 'eip155:56' as const,
  chainNamespace: 'eip155' as const,
  name: 'BNB Smart Chain',
  nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
  rpcUrls: {
    default: {
      http: IS_LOCALHOST
        ? ['https://bsc-rpc.publicnode.com']
        : ['https://bsc-mainnet.core.chainstack.com/2cf082a0fb6acc2d50a9166da4786f4d'],
      webSocket: IS_LOCALHOST
        ? ['wss://bsc-rpc.publicnode.com']
        : ['wss://bsc-mainnet.core.chainstack.com/2cf082a0fb6acc2d50a9166da4786f4d'],
    },
  },
  blockExplorers: {
    default: { name: 'BscScan', url: 'https://bscscan.com' },
  },
} as const;

export const BSC_TESTNET_NETWORK = {
  id: 97,
  chainId: 97,
  caipNetworkId: 'eip155:97' as const,
  chainNamespace: 'eip155' as const,
  name: 'BNB Smart Chain Testnet',
  nativeCurrency: { name: 'tBNB', symbol: 'tBNB', decimals: 18 },
  rpcUrls: {
    default: {
      http: ['https://bsc-testnet-rpc.publicnode.com'],
      webSocket: ['wss://bsc-testnet-rpc.publicnode.com'],
    },
  },
  blockExplorers: {
    default: { name: 'BscScan', url: 'https://testnet.bscscan.com' },
  },
} as const;

/**
 * Simplified chain config using centralized network objects
 */
const CURRENT_NETWORK = IS_MAINNET ? BSC_MAINNET_NETWORK : BSC_TESTNET_NETWORK;

export const CHAIN_CONFIG: ChainConfig = {
  accountLabel: 'BNB Smart Chain Account',
  name: CURRENT_NETWORK.name,
  chainId: CURRENT_NETWORK.chainId,
  rpcUrl: CURRENT_NETWORK.rpcUrls.default.http[0],
  wsRpcUrl: CURRENT_NETWORK.rpcUrls.default.webSocket[0],
  nativeCurrency: CURRENT_NETWORK.nativeCurrency,
};

/**
 * RPC URLs
 */
export const HTTP_RPC_URL = CHAIN_CONFIG.rpcUrl;
export const WSS_RPC_URL = CHAIN_CONFIG.wsRpcUrl;

/**
 * Detect if running in a testing/headless environment (e.g., PageSpeed Insights, Lighthouse)
 * These environments often block WebSocket connections, causing console errors
 */
const isHeadless = () => {
  if (typeof navigator === 'undefined') return true;
  const ua = navigator.userAgent;

  // Check for headless browsers and performance testing tools
  if (/HeadlessChrome|PhantomJS|Lighthouse|PageSpeed|Chrome-Lighthouse|PTST/i.test(ua) || ua.includes('Speed Insights')) {
    return true;
  }

  // Additional check: if WebGL is disabled (common in headless environments)
  if (typeof window !== 'undefined') {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) return true;
  }

  return false;
};

/**
 * Enable WebSocket connections for real-time updates
 * Automatically disabled in headless/testing environments to prevent console errors
 */
export const ENABLE_WEBSOCKET = !isHeadless();

/**
 * Delay before initializing WebSocket connection (ms)
 * Prevents WebSocket from competing with critical page load resources
 */
export const WEBSOCKET_INIT_DELAY_MS = 2000;

/**
 * Smart Contract Addresses
 */
export const MARKET_CONTRACT_ADDRESS = IS_MAINNET
  ? '0x3d36F31F512891671Ce950b0F823cF01B62C5E8A'
  : '0x73E8107C212BB295b8E17aB391deC23eFC31B09a';
export const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11'; // Multicall3 is deployed at the same address on both testnet and mainnet

/**
 * External Contract Addresses
 */
export const PANCAKESWAP_USDC_ADDRESS = IS_MAINNET
  ? '0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d' // Mainnet: Official USDC
  : '0x9901d678a9356d6DB316509059ED7A6F4a06a4fc'; // Testnet: Custom token
export const PANCAKESWAP_SWAP_URL = `https://pancakeswap.finance/swap?outputCurrency=${PANCAKESWAP_USDC_ADDRESS}`;

/**
 * Community & Support URLs
 */
export const DISCORD_INVITE_URL = 'https://discord.gg/h8bDaEhWkZ';

// ============================================================================
// TOKEN CONFIGURATION
// ============================================================================

export interface TokenConfig {
  symbol: string;
  name: string;
  decimals: number;
  logoPath: string;
}

/**
 * Default token decimals to use when the prediction decimals cannot be fetched from the blockchain
 */
export const DEFAULT_TOKEN_DECIMALS = 18;

/**
 * Default currency symbol to use when the prediction currency cannot be fetched from the blockchain
 */
export const DEFAULT_CURRENCY_SYMBOL = 'Token';

/**
 * Get token configuration dynamically
 * For prediction currency, this should be fetched from useTokenInfo
 */
export function getTokenConfig(tokenSymbol: string, decimals: number, name?: string): TokenConfig {
  // Check if it's the native currency
  if (tokenSymbol === CHAIN_CONFIG.nativeCurrency.symbol) {
    return {
      symbol: CHAIN_CONFIG.nativeCurrency.symbol,
      name: CHAIN_CONFIG.nativeCurrency.name,
      decimals: CHAIN_CONFIG.nativeCurrency.decimals,
      logoPath: 'img/BNB.svg',
    };
  }

  // Map token symbols to their logo paths
  const logoMap: Record<string, string> = {
    USDC: 'img/USDC.svg',
  };

  return {
    symbol: tokenSymbol,
    name: name || tokenSymbol,
    decimals: decimals,
    logoPath: logoMap[tokenSymbol] ?? 'img/apple-touch-icon.png',
  };
}

// ============================================================================
// API ENDPOINTS
// ============================================================================

export const PRICE_API_URL = 'https://flamingo-us-1.b-cdn.net/flamingo/live-data/prices/latest';
export const BLOB_STORAGE_URL = 'https://blob.neutrablob.com/v1/blobs';
export const BLOB_CDN_URL = 'https://neutrablob.b-cdn.net/v1/blobs';

// ============================================================================
// TIMEOUTS & DELAYS (in milliseconds)
// ============================================================================

/**
 * Retry delay for blob storage operations
 */
export const BLOB_STORAGE_RETRY_DELAY_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Delay before starting batch rendering of additional items (milliseconds)
 */
export const BATCH_RENDER_START_DELAY_MS = 50;

/**
 * Delay between batch rendering increments (milliseconds)
 */
export const BATCH_RENDER_INCREMENT_DELAY_MS = 50;

/**
 * Poll balance/allowance (fallback only)
 */
export const BALANCE_POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Cooldown period for play account top-ups (1 day)
 */
export const PLAY_ACCOUNT_TOP_UP_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 1 day

/**
 * Default timeout for WebSocket event listeners before falling back to polling
 */
export const WEBSOCKET_EVENT_TIMEOUT_MS = 10000; // 10 seconds

/**
 * Delay after receiving blockchain events before triggering balance refresh
 * Brief delay ensures RPC node state is updated (nodes may have cached state)
 * WebSocket events fire ~instantly, but node needs time to process
 */
export const BALANCE_REFRESH_DELAY_MS = 500; // 500ms

/**
 * Debounce window for balance/allowance refresh triggers
 * Prevents duplicate fetches when multiple triggers fire in quick succession
 * (e.g., approval + market creation within seconds)
 */
export const BALANCE_REFRESH_DEBOUNCE_MS = 300; // 300ms

/**
 * Base delay for WebSocket reconnection attempts (exponential backoff starts here)
 */
export const WEBSOCKET_RECONNECT_BASE_DELAY_MS = 1000; // 1 second

/**
 * Maximum delay between WebSocket reconnection attempts (for exponential backoff)
 */
export const WEBSOCKET_RECONNECT_MAX_DELAY_MS = 30000; // 30 seconds

/**
 * Long-term retry delay after max attempts exceeded (keeps trying indefinitely)
 */
export const WEBSOCKET_LONG_TERM_RETRY_DELAY_MS = 60000; // 60 seconds

/**
 * Maximum number of WebSocket reconnection attempts before switching to long-term retry mode
 */
export const WEBSOCKET_RECONNECT_MAX_ATTEMPTS = 10; // After this, retry every 60s

/**
 * HTTP polling interval when WebSocket is connected and healthy (slower fallback)
 * Reduced frequency since WebSocket provides instant updates
 */
export const HTTP_POLLING_INTERVAL_WITH_WS_MS = 10000; // 10 seconds

/**
 * HTTP polling interval when WebSocket is disconnected (faster fallback)
 * Faster polling compensates for lack of instant WebSocket updates
 */
export const HTTP_POLLING_INTERVAL_WITHOUT_WS_MS = 1000; // 1 second

/**
 * Health check interval for monitoring WebSocket connection quality
 */
export const WEBSOCKET_HEALTH_CHECK_INTERVAL_MS = 60000; // 1 minute

/**
 * Keepalive interval for WebSocket connections (prevents server timeout on idle connections)
 * Sends periodic lightweight queries to keep connection active
 */
export const WEBSOCKET_KEEPALIVE_INTERVAL_MS = 30000; // 30 seconds

/**
 * Retry delays for balance/allowance fetching (first retry, subsequent retries)
 */
export const BALANCE_FETCH_RETRY_DELAYS_MS = [500, 1000];

/**
 * How often to check for unsettled markets
 */
export const UNSETTLED_MARKET_CHECK_INTERVAL_MS = 30000; // 30 seconds

/**
 * UI Interaction Timeouts (in milliseconds)
 */
export const POPUP_AUTO_DISMISS_MS = 5000; // Auto-dismiss popup notifications
export const SUCCESS_OVERLAY_DELAY_MS = 2000; // Delay before closing success overlays (use requireManualDismiss=true to disable auto-dismiss)
export const CLIPBOARD_COPY_FEEDBACK_MS = 2000; // Duration to show "Copied!" feedback
export const ANIMATION_TRIGGER_DELAY_MS = 10; // Delay before triggering mount animations
export const MOBILE_VIBRATION_MS = 30; // Duration of mobile haptic feedback
export const CLOCK_UPDATE_INTERVAL_MS = 1000; // Update clock displays every second
export const DOM_SCROLL_DELAY_MS = 100; // Delay for DOM updates before scrolling to element
export const SCROLL_DEBOUNCE_MS = 100; // Debounce delay for scroll event handlers

/**
 * Time window for event deduplication (prevents duplicate event processing)
 * Must be > HTTP_POLLING_INTERVAL_WITH_WS_MS (10s) to handle WebSocket reconnection scenarios
 * where HTTP polling temporarily overlaps with WebSocket event delivery
 */
export const EVENT_DEDUPLICATION_WINDOW_MS = 15000; // 15 seconds

/**
 * Time to keep market creation notifications in memory before cleanup
 * Prevents duplicate MARKET_CREATED events for the same market across sessions
 * 24 hours is sufficient for typical user sessions while preventing unbounded memory growth
 */
export const MARKET_CREATION_NOTIFICATION_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Polling interval for creator markets (time between checking for new markets)
 */
export const CREATOR_MARKETS_POLL_INTERVAL_MS = 10 * 1000; // 10 seconds

/**
 * Polling interval for prediction history (time between checking for prediction updates)
 */
export const PREDICTION_HISTORY_POLL_INTERVAL_MS = 10 * 1000; // 10 seconds

/**
 * Milliseconds in one hour (for time calculations)
 */
export const MILLISECONDS_PER_HOUR = 1000 * 60 * 60;

/**
 * Maximum age of events to show popup notifications (5 minutes)
 */
export const POPUP_NOTIFICATION_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * Debounce delay for draft autosave (prevents saving on every keystroke)
 */
export const DRAFT_AUTOSAVE_DEBOUNCE_MS = 500; // 500ms

// ============================================================================
// UI SETTINGS
// ============================================================================

/**
 * Maximum number of unread notifications to keep active
 */
export const MAX_UNREAD_NOTIFICATIONS = 99;

/**
 * Threshold for alerting creators about unsettled markets after deadline
 */
export const UNSETTLED_MARKET_ALERT_THRESHOLD_MS = 12 * 60 * 60 * 1000; // 12 hours

// ============================================================================
// CACHE DURATIONS (in milliseconds)
// ============================================================================

/**
 * How long to cache contract configuration (platform fees, min prediction, etc.)
 * INVALIDATION TRIGGERS: Never (permanent cache)
 */
export const CONTRACT_CONFIG_CACHE_DURATION_MS = Infinity; // Permanent (never expires)

/**
 * How long to cache balance and allowance data
 * INVALIDATION TRIGGERS:
 * - Approval event (token allowance changed)
 * - Transfer event (incoming/outgoing token transfers)
 * - PlacedPrediction event (tokens spent on prediction)
 * - CreatedMarket event (tokens spent on market creation)
 * - SettledMarket event (tokens received from winnings)
 * - CancelledMarket event (tokens refunded)
 * - CreatedCreator event (registration fee spent)
 * - UpdatedCreator event (update fee spent)
 * - Account switch
 */
export const BALANCE_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How long to cache token prices
 * INVALIDATION TRIGGERS: None (TTL only)
 */
export const PRICE_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How long to cache user prediction counts
 * INVALIDATION TRIGGERS:
 * - PlacedPrediction event (user's prediction count increased)
 * - Account switch
 */
export const PREDICTION_COUNT_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How long to cache market data
 * INVALIDATION TRIGGERS:
 * - CreatedMarket event (new market added)
 * - SettledMarket event (market state changed to settled)
 * - CancelledMarket event (market state changed to cancelled)
 * - PlacedPrediction event (market stats updated - pool sizes, totals)
 * - Account switch
 */
export const MARKET_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How long to cache creator data (both paginated list and individual lookups)
 * Applies to both getCreators() and getCreatorDetails()
 * INVALIDATION TRIGGERS:
 * - CreatedMarket event (creator's last activity updated)
 * - SettledMarket event (creator's data may be affected)
 * - PlacedPrediction event (creator's data may be affected)
 * - CancelledMarket event (creator's data may be affected)
 * - CreatedCreator event (new creator registered)
 * - UpdatedCreator event (creator info changed - nickname, description, stream URL)
 * - Account switch
 */
export const CREATOR_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How long to cache homepage stats (total creators, markets, predictions count)
 * INVALIDATION TRIGGERS:
 * - CreatedMarket event (markets count increased)
 * - PlacedPrediction event (predictions count increased)
 * - CreatedCreator event (creators count increased)
 */
export const HOMEPAGE_STATS_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How long to cache creator stats (volume, earnings, settled markets, predictions, last activity)
 * INVALIDATION TRIGGERS:
 * - SettledMarket event (volume, earnings, settled markets count change)
 * - PlacedPrediction event (predictions count, last activity change)
 * - CreatedMarket event (last activity change)
 * - Account switch
 */
export const CREATOR_STATS_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * How long to cache creator address by ID mappings (permanent since these never change)
 * Creator IDs are sequential and once assigned to an address, the mapping is immutable
 * INVALIDATION TRIGGERS: Never (permanent cache)
 */
export const CREATOR_ADDRESS_BY_ID_CACHE_DURATION_MS = Infinity; // Permanent (never expires)

/**
 * How long to cache creator counts (all/live/pending/settled markets count)
 * INVALIDATION TRIGGERS: CreatedMarket, SettledMarket, CancelledMarket events
 */
export const CREATOR_COUNTS_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Maximum number of markets to keep in the market cache (LRU eviction)
 * When cache exceeds this limit, least recently used entries are removed
 * 1000 markets × ~5KB average = ~5MB (conservative memory limit)
 */
export const MARKET_CACHE_MAX_SIZE = 1000;

/**
 * Maximum number of settlement calculations to keep in cache (LRU eviction)
 * Settlement calculations are expensive, so we cache them aggressively
 * Since settled markets are immutable, cache can be permanent once calculated
 */
export const MAX_SETTLEMENT_CACHE_SIZE = 1000;

/**
 * Maximum number of market contexts to keep in cache (LRU eviction)
 * Market context (aggregated prediction totals) is expensive to compute
 * Used by settlement calculations to avoid redundant aggregations
 * Settled markets are immutable and can be cached forever
 */
export const MAX_SETTLEMENT_CONTEXT_CACHE_SIZE = 1000;

// ============================================================================
// EVENTS PAGE CONFIGURATION
// ============================================================================

/**
 * Max blocks per RPC eth_getLogs call (Chainstack growth plan limit is 10,000 blocks)
 */
export const EVENTS_PAGE_CHUNK_SIZE = 9999;

/**
 * Time range presets for Events page
 * BSC Maxwell hardfork: 0.75s block times
 */
export const EVENTS_PAGE_TIME_RANGES = {
  '5m': { blocks: 400, label: '5 minutes', enabled: true }, // 5 * 60 / 0.75 = 400 blocks (~5 RPC calls)
  '15m': { blocks: 1200, label: '15 minutes', enabled: true }, // 15 * 60 / 0.75 = 1200 blocks (~13 RPC calls)
  '1h': { blocks: 4800, label: '1 hour', enabled: true }, // 1 * 3600 / 0.75 = 4800 blocks (~51 RPC calls) - DISABLED until backend indexer
  '4h': { blocks: 19200, label: '4 hours', enabled: true }, // 4 * 3600 / 0.75 = 19200 blocks (~202 RPC calls) - DISABLED until backend indexer
} as const;

export type EventsPageTimeRange = keyof typeof EVENTS_PAGE_TIME_RANGES;

/**
 * Default time range for Events page
 */
export const EVENTS_PAGE_DEFAULT_TIME_RANGE: EventsPageTimeRange = '5m';

/**
 * How many blocks to look back for blockchain events on the Events page
 * Default to 4 hours to balance history vs. RPC calls
 */
export const EVENTS_PAGE_BLOCK_RANGE = EVENTS_PAGE_TIME_RANGES[EVENTS_PAGE_DEFAULT_TIME_RANGE].blocks;

/**
 * How many blocks to look back for the lightweight txHash backfill
 * ~10 minutes worth of blocks at 0.75s/block = 800 blocks
 */
export const EVENTS_PAGE_BACKFILL_BLOCK_RANGE = 800;

/**
 * How long to cache events page data (match longest time range + buffer)
 */
export const EVENTS_PAGE_CACHE_DURATION_MS = 5 * 60 * 60 * 1000; // 5 hours

/**
 * Fallback polling interval for events page when WebSocket is disconnected
 */
export const EVENTS_PAGE_FALLBACK_POLL_INTERVAL_MS = 30 * 1000; // 30 seconds

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/**
 * Number of creators to fetch per page
 */
export const CREATORS_PAGE_SIZE = 100;

/**
 * Number of predictions to fetch per page
 */
export const PREDICTIONS_PAGE_SIZE = 25;

/**
 * Number of markets to fetch per page for a creator
 */
export const CREATOR_MARKETS_PAGE_SIZE = 100;

/**
 * Number of items to display per page in UI pagination
 * (Applies to: market lists, predictions, etc.)
 */
export const UI_PAGE_SIZE = 20;

/**
 * Initial number of items to render in lists to prevent lag with large datasets
 * (Applies to: market cards, drafts, prediction history)
 */
export const BATCH_RENDER_INITIAL_COUNT = 25;

/**
 * Maximum markets to fetch in a single Multicall3 batch for rules fetching
 * Prevents RPC/gas limits when fetching rules for many markets at once
 */
export const MULTICALL_BATCH_SIZE = 100;

// ============================================================================
// TOKEN BALANCE & APPROVAL
// ============================================================================

/**
 * Gas reserve to keep when sending max native currency (e.g., BNB)
 * Reserves approximately 0.001 BNB for transaction gas fees
 */
export const NATIVE_CURRENCY_GAS_RESERVE = 0.001;

/**
 * Default approval amount as a string (10000 tokens)
 * Use getDefaultApprovalAmount(decimals) to get the bigint value with the correct decimals
 */
export const DEFAULT_APPROVAL_AMOUNT_STRING = '10000';

/**
 * Get the default approval amount with the specified decimals
 * @param decimals - The number of decimals for the token
 * @returns The approval amount as a bigint
 */
export function getDefaultApprovalAmount(decimals: number): bigint {
  return BigInt(DEFAULT_APPROVAL_AMOUNT_STRING) * 10n ** BigInt(decimals);
}

/**
 * Threshold for considering a token "fully approved" (1 million tokens)
 */
export const LARGE_ALLOWANCE_THRESHOLD = 1000000;

/**
 * Play account starting balance (10,000 tokens with 18 decimals)
 */
export const PLAY_ACCOUNT_STARTING_BALANCE = BigInt('10000000000000000000000'); // 10,000 tokens

/**
 * Play account top-up amount (10,000 tokens)
 */
export const PLAY_ACCOUNT_TOP_UP_AMOUNT = 10000;

// ============================================================================
// FILE UPLOAD LIMITS
// ============================================================================

/**
 * Maximum base64-encoded file size for blob storage (10MB)
 */
export const MAX_BASE64_FILE_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Base64 encoding overhead factor (~33% increase)
 */
export const BASE64_ENCODING_OVERHEAD = 1.34;

/**
 * Calculate maximum raw file size based on base64 limit
 */
export const MAX_RAW_FILE_SIZE_MB = MAX_BASE64_FILE_SIZE_BYTES / BASE64_ENCODING_OVERHEAD / 1024 / 1024;

// ============================================================================
// ANIMATION DURATIONS (in milliseconds)
// ============================================================================

export const ANIMATION_DURATIONS = {
  floatNumberUp: 1200,
  shineAcross: 1000,
  explodingConfetti: 1000,
  stepDelayMin: 350,
  stepDelayMax: 830, // 350 + 480
};

// ============================================================================
// RESPONSIVE BREAKPOINTS (in pixels)
// ============================================================================

export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
};

// ============================================================================
// FEE CALCULATIONS
// ============================================================================

/**
 * Basis points divisor for fee calculations (1 bps = 0.01%)
 */
export const BASIS_POINTS_DIVISOR = 10000;

// ============================================================================
// THIRD PARTY INTEGRATIONS
// ============================================================================

/**
 * PostHog API key for analytics tracking
 */
export const POSTHOG_API_KEY = 'phc_IWUYnt4lRfN4EvmzxJc6JjzpabAr3g3rAO3l81BCCwS';

/**
 * PostHog API host URL for sending analytics events
 */
export const POSTHOG_API_HOST = 'https://t.flm.fun';

/**
 * PostHog UI host URL
 */
export const POSTHOG_UI_HOST = 'https://us.i.posthog.com';

/**
 * Defaults for PostHog analytics
 */
export const POSTHOG_DEFAULTS = '2026-01-30';
